import ComponentManager, {ComponentOptions, ComponentOptionsValuePlaceholder} from "../../componentFramework";
import ObjectDataStore from "../../scripts/ObjectDataStore";
import EditContent from "../EditContent";
import EventListenerManager from "../EventListenerManager";
import ElementAndSubtreeObserver from "../../scripts/ElementAndSubtreeObserver";
import Message from "../Message";
import EditContentNodeControls from "../EditContentNodeControls";
import EditContentNodes, {ComponentStaticEventMap} from "../EditContentNodes";
import EditContentGroup from "../EditContentGroup";

enum DataStoreKeys {
	Saving = "saving",
	Saved = "saved"
}

const defaultOptions = {
	nodeId: ComponentOptionsValuePlaceholder.number.required,
	depth: ComponentOptionsValuePlaceholder.number.required,
	definitionName: ComponentOptionsValuePlaceholder.string.optional
};
export type DefaultOptionsType = typeof defaultOptions;

/**
 */
@ComponentManager.register({requires: () => EditContentNodeControls})
export default class EditContentNode {
	public static componentStyleClass = "editContentNode";

	protected readonly options: ComponentOptions<DefaultOptionsType>;

	private static readonly focusedStyleClass = "editContentNode--focused";

	private static readonly isChangedStyleClass = "isChanged";
	private static readonly isSavingStyleClass = "isSaving";
	private static readonly jsfComponentStyleClass = "editContentNode__component"; // Marks child nodes which are JSF components.

	private static dataStore: ObjectDataStore<{ [DataStoreKeys.Saving]: string|boolean, [DataStoreKeys.Saved]: string|boolean }>;

	private executesForJsfAjax: Array<string> | undefined;
	private rendersForJsfAjax: Array<string> | undefined;

	private changed = false;
	private saving = false;

	private readonly eventListenerManager = new EventListenerManager();

	private readonly elementAndSubtreeObserver;
	private readonly inputs;
	private readonly selects;
	private readonly textAreas;

	@ComponentManager.on("beforeFirstConstruction")
	public static beforeFirstConstruction() {
		EditContentNode.dataStore = new ObjectDataStore();
	}

	constructor(public readonly rootElement: HTMLElement) {
		this.options = new ComponentOptions(this, rootElement, defaultOptions);
		this.elementAndSubtreeObserver = ElementAndSubtreeObserver.getInstance<any>(rootElement);

		// Monitor for HTML fields to setup listeners
		this.inputs = this.elementAndSubtreeObserver.onElementWithTagName("input", this.attachFieldListener, this.detachFieldListener).getLiveCollection();
		this.selects = this.elementAndSubtreeObserver.onElementWithTagName("select", this.attachFieldListener, this.detachFieldListener).getLiveCollection();
		this.textAreas = this.elementAndSubtreeObserver.onElementWithTagName("textarea", this.attachFieldListener, this.detachFieldListener).getLiveCollection();

		// Clear stored data if an element for a JSF Component is added or removed
		this.elementAndSubtreeObserver.onElementWithStyleClass(EditContentNode.jsfComponentStyleClass, this.clearJsfAjaxCache, this.clearJsfAjaxCache);

		// Watch for field message addition and removal
		const rootElementManaged = this.eventListenerManager.manage(rootElement);

		const onFieldMessageEvent = () => {
			this.dispatchElementEvent("editContentNodeFieldMessageChange");
		};
		rootElementManaged.addEventListener("fieldMessageAdded", onFieldMessageEvent);
		rootElementManaged.addEventListener("fieldMessageRemoved", onFieldMessageEvent);

		// Watch for focus event inside this node.
		// Only one item can have a focus at a time so we don't remove focus on "focusout" but wait for another to
		// take focus
		rootElementManaged.addEventListener("focusin", () => {
			this.focus();
		});
		rootElementManaged.addEventListener("pseudoFocusIn", () => {
			// Triggered by custom code (e.g. Wysiwyg)
			this.focus();
		});

		// Watch for focus change made by EditContentNodeControls.
		// Calling EditContentNode.focus() will actually tell EditContentNodeControls to update focus
		// and it will then trigger the "focusChange" event which is when we do our style updates,
		// and when we trigger the "editContentNodeFocusChange" DOM event which is watched by EditContentGroup
		// which will then also update its own styling.
		this.eventListenerManager.manage(EditContentNodes).addEventListener(
			"focusChange", this.onEditContentNodeControlsFocusChange
		);
	}

	@ComponentManager.on("ready")
	protected componentReady() {
		// Store current values, assuming the values at time of construction are already saved to the server
		this.storeCurrentValues(DataStoreKeys.Saved, true);

		// Give this area focus
		this.eventListenerManager.manage(this.rootElement).addEventListener("click", (event) => {
			this.focus();
			event.stopPropagation();
		});

		// Dispatch event to notify other components through a DOM event that this node has been all setup and is ready.
		this.dispatchElementEvent("editContentNodeReady");
	}

	protected dispatchElementEvent(eventType: "editContentNodeReady" | "editContentNodeFocusChange" | "editContentNodeFieldMessageChange") {
		this.rootElement.dispatchEvent(
			new CustomEvent(eventType,
				{
					bubbles: true,
					cancelable: false,
					detail: {
						editContentNode: this
					}
				})
		);
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.eventListenerManager.removeListeners();
		this.clearJsfAjaxCache();
	}

	containsMessage(): boolean {
		return Message.containsMessage(this.rootElement);
	}

	private detachFieldListener = (field: Element) => {
		this.eventListenerManager.removeListeners(field);
	}

	private attachFieldListener = (field: Element) => {
		if (this.eventListenerManager.doesManage(field)) {
			// Field already has a listener
			return;
		}

		const managedField = this.eventListenerManager.manage(field);

		const defaultView = field.ownerDocument.defaultView;
		if (defaultView !== null) {
			// Inputs
			if (field instanceof defaultView.HTMLInputElement) {
				if (field.type !== "button" && field.type !== "reset" && field.type !== "submit" && field.type !== "image") {
					managedField.addEventListener("change", this.onFieldChange);
					if (field.type !== "checkbox" && field.type !== "radio") {
						managedField.addEventListener("input", this.onFieldInput);
					}
				}
			}

			// Selects
			else if (field instanceof defaultView.HTMLSelectElement) {
				managedField.addEventListener("change", this.onFieldChange);
			}

			// Text Areas
			else if (field instanceof defaultView.HTMLTextAreaElement) {
				managedField.addEventListener("input", this.onFieldInput);
				managedField.addEventListener("change", this.onFieldChange);
			}
		}
	}

	private onFieldInput = () => {
		const valueChanged = this.hasAValueChanged();
		this.setChanged(valueChanged);
	}
	private onFieldChange = () => {
		const valueChanged = this.hasAValueChanged();
		this.setChanged(valueChanged);
		if (valueChanged) {
			EditContent.getInstance(this.getElement())?.save();
		}
	}

	public getDepth(): number {
		return this.options.getValues().depth;
	}
	public getDefinitionName(): string {
		return this.options.getValues().definitionName;
	}

	public getElement(): HTMLElement {
		return this.rootElement;
	}

	private clearJsfAjaxCache = () => {
		this.executesForJsfAjax = undefined;
		this.rendersForJsfAjax = undefined;
	}
	public getExecutesForJsfAjax(): Array<string> {
		if (this.executesForJsfAjax === undefined) {
			this.executesForJsfAjax = new Array<string>();
			const jsfElems = this.getElement().getElementsByClassName(EditContentNode.jsfComponentStyleClass);
			for (let i = 0; i < jsfElems.length; i++) {
				let id = jsfElems[i]!.id;
				if (id.length !== 0) {
					this.executesForJsfAjax.push(id);
				}
			}
		}
		return this.executesForJsfAjax;
	}
	public getRendersForJsfAjax(): Array<string> {
		if (this.rendersForJsfAjax === undefined) {
			this.rendersForJsfAjax = new Array<string>();
			const doc = this.getElement().ownerDocument;
			const nodeExecutes = this.getExecutesForJsfAjax();
			for (let j = 0; j < nodeExecutes.length; j++) {
				const messageElem = doc.querySelector(`[data-message-for="${nodeExecutes[j]}"]`);
				if (messageElem !== null && messageElem.id.length !== 0) {
					this.rendersForJsfAjax.push(messageElem.id);
				}
			}
		}
		return this.rendersForJsfAjax;
	}

	public onSaving = () => {
		if (this.rootElement === undefined) {
			return; // This EditContentNode's destroy() was likely called.
		}
		this.setSaving(true);
		this.storeCurrentValues(DataStoreKeys.Saving);
	}
	public onSaved = () => {
		if (this.rootElement === undefined) {
			return; // This EditContentNode's destroy() was likely called.
		}
		this.setSaving(false);
		this.storeCurrentValues(DataStoreKeys.Saved);
		const valueChanged = this.hasAValueChanged();
		this.setChanged(valueChanged);
	}
	public onErrorDuringSave = () => {
		// Nothing different to do. This will update the stored "saved" version but that is really the version last sent to the server, not necessarily the "saved" version.
		this.onSaved();
	}

	public isChanged(): boolean {
		return this.changed;
	}
	private setChanged(isChanged: boolean) {
		this.changed = isChanged;
		if (isChanged) {
			EditContent.getInstance(this.getElement())?.addToSave(this);
			this.rootElement.classList.add(EditContentNode.isChangedStyleClass);
		} else {
			EditContent.getInstance(this.getElement())?.removeFromSave(this);
			this.rootElement.classList.remove(EditContentNode.isChangedStyleClass);
		}
	}

	public save() {
		this.setChanged(true);
	}

	public isSaving(): boolean {
		return this.saving;
	}
	private setSaving(isSaving: boolean) {
		this.saving = isSaving;
		this.rootElement.classList.toggle(EditContentNode.isSavingStyleClass, isSaving);
	}

	public isFocused(): boolean {
		return this.rootElement.classList.contains(EditContentNode.focusedStyleClass);
	}
	public focus() {
		if (!this.isFocused()) {
			const nodePath = this.options.getValues().nodeId;
			const nodeControls = EditContentNodeControls.getInstance(nodePath);
			if (nodeControls !== undefined) {
				nodeControls.focus(); // This will end up causing onEditContentNodeControlsFocusChange
			} else {
				console.error(`Could not find node controls for node with nodePath of "${nodePath}", so could not update focus.`);
			}
		}
	}

	private onEditContentNodeControlsFocusChange = (data: ComponentStaticEventMap["focusChange"]) => {
		const focusing = data.nodeId === this.options.getValues().nodeId;
		if (focusing !== this.isFocused()) {
			this.rootElement.classList.toggle(EditContentNode.focusedStyleClass, focusing);
			this.dispatchElementEvent("editContentNodeFocusChange");
		}
	}

	private storeCurrentValues(storeKey: DataStoreKeys, isInit = false) {

		// Inputs
		for (let i = 0; i < this.inputs.length; i++) {
			let input = this.inputs[i]!;
			if (input.type === "checkbox" || input.type === "radio") {
				// Note: While radios are almost always part of a group, we don't care for this. We just want to know if the boolean value of any of those individual options changed.
				this.storeValueOnField(input, storeKey, input.checked, isInit);
			} else if (input.type !== "button" && input.type !== "reset" && input.type !== "submit" && input.type !== "image") {
				this.storeValueOnField(input, storeKey, input.value, isInit);
			}
		}

		// Selects
		for (let i = 0; i < this.selects.length; i++) {
			const select = this.selects[i]!;
			const currentValue = EditContentNode.getValueOfSelect(select);
			this.storeValueOnField(select, storeKey, currentValue, isInit);
		}

		// Text Areas
		for (let i = 0; i < this.textAreas.length; i++) {
			const textArea = this.textAreas[i]!;
			this.storeValueOnField(textArea, storeKey, textArea.value, isInit);
		}
	}

	private storeValueOnField(field: Element, storeKey: DataStoreKeys, currentFieldValue: string|boolean, isInit: boolean) {
		let value = currentFieldValue;
		if (!isInit && storeKey === DataStoreKeys.Saved) {
			let savingValue = EditContentNode.getDataStore().remove(field, DataStoreKeys.Saving);
			if (savingValue !== undefined) {
				value = savingValue;
			}
		}
		EditContentNode.getDataStore().set(field, storeKey, value);

		const displayableValueLocation
			= this.rootElement.querySelector(":scope [data-displayable-value-location]")?.getAttribute("data-displayable-value-location");
		if (displayableValueLocation === null || displayableValueLocation === undefined || displayableValueLocation === "@value") {
			const potentialGroup = EditContentNode.resolvePotentialEditContentGroup(this.rootElement);
			if (potentialGroup !== null) {
				ComponentManager.getComponent(potentialGroup as HTMLElement, EditContentGroup)?.updateSubLabel(this, value);
			}
		} else if (displayableValueLocation !== "@none" && displayableValueLocation.length > 0 && isInit) {
			const lastAt = displayableValueLocation.lastIndexOf('@');
			const displayableValueElementClassName = lastAt > -1 ? displayableValueLocation.substring(0, lastAt) : displayableValueLocation;
			const updateWrappingGroupSublabel = (ele: Element) => {
				const calculatedValue = lastAt > -1 ? ele.getAttribute(displayableValueLocation.substring(lastAt + 1)) : ele.textContent;
				if (calculatedValue !== null) {
					const reResolvedPotentialGroup = EditContentNode.resolvePotentialEditContentGroup(this.rootElement);
					if (reResolvedPotentialGroup !== null) {
						ComponentManager.getComponent(reResolvedPotentialGroup as HTMLElement, EditContentGroup)?.updateSubLabel(this, calculatedValue);
					}
				}
			};

			const initialValueElement = this.rootElement.querySelector(":scope ." + displayableValueElementClassName);
			if (initialValueElement !== null) {
				updateWrappingGroupSublabel(initialValueElement);
			}
			this.elementAndSubtreeObserver.onElementWithStyleClass(displayableValueElementClassName, updateWrappingGroupSublabel);
		}
	}

	private static resolvePotentialEditContentGroup(ele: Element|null): Element|null {
		if (ele !== null) {
			let potentialGroup: Element | null = ele;
			do {
				potentialGroup = potentialGroup.parentElement;
			} while (potentialGroup !== null && !potentialGroup.classList.contains(EditContentGroup.componentStyleClass));
			return potentialGroup;
		} else {
			return null;
		}
	}

	private hasAValueChanged(): boolean {
		// Inputs
		for (let i = 0; i < this.inputs.length; i++) {
			const input = this.inputs[i]!;
			if (input.type === "button" || input.type === "reset" || input.type === "submit" || input.type === "image") {
				continue;
			}
			if (input.type === "checkbox" || input.type === "radio") {
				// Note: While radios are almost always part of a group, we don't care for this. We just want to know if the boolean value of any of those individual options changed.
				if (EditContentNode.hasFieldValueChanged(input, input.checked)) {
					return true;
				}
			} else {
				if (EditContentNode.hasFieldValueChanged(input, input.value)) {
					return true;
				}
			}
		}

		// Selects
		for (let i = 0; i < this.selects.length; i++) {
			const select = this.selects[i]!;
			const currentValue = EditContentNode.getValueOfSelect(select);
			if (EditContentNode.hasFieldValueChanged(select, currentValue)) {
				return true;
			}
		}

		// Text Areas
		for (let i = 0; i < this.textAreas.length; i++) {
			const textArea = this.textAreas[i]!;
			if (EditContentNode.hasFieldValueChanged(textArea, textArea.value)) {
				return true;
			}
		}

		return false;
	}

	public getSavedValue(): string|boolean {
		return EditContentNode.getDataStore().get(this.rootElement, DataStoreKeys.Saved);
	}

	private static hasFieldValueChanged(field: HTMLElement, currentValue: string|boolean): boolean {
		const value = EditContentNode.getDataStore().get(field, DataStoreKeys.Saving);
		if (value === undefined) { // If we don't have a value that we are currently saving, test against the saved value
			return currentValue !== EditContentNode.getDataStore().get(field, DataStoreKeys.Saved);
		}
		return currentValue !== value;

		/* TODO (Michael): Html is pretty much always not the same because CKEditor converts characters into entities.
		    I tried the following. It worked for simple HTML, but when it got a little more complex it did not.
		    What if I use "textContent" instead of "innerHTML" and removed script tags and maybe some other things?
		    I do something like this in JsfAjaxScrollImprovements.ts.
		if (testUsingIsEqualNode === false || typeof currentValue === "boolean") {
		}

		// This will take care of differences like entities vs character
		const doc = field.ownerDocument;
		const savedValueNode = doc.createElement("div");
		savedValueNode.innerHTML = savedValue as string;
		const currentValueNode = doc.createElement("div");
		currentValueNode.innerHTML = currentValue as string;

		return savedValueNode.isEqualNode(currentValueNode) !== true;
		 */
	}

	/**
	 * If multiple selections then the returned value is joined into one string.
	 * @param select
	 * @private
	 */
	private static getValueOfSelect(select: HTMLSelectElement): string {
		if (!select.multiple) {
			return select.value;
		}
		let value = "";
		for (let i=0; i < select.options.length; i++) {
			const option = select.options[i]!;
			if (option.selected) {
				value += (option.value || option.text);
			}
		}
		return value;
	}
	
	public static getDataStore() {
		return EditContentNode.dataStore;
	}
}
